import { apiUrl } from '../../config/runtime';
import { apiFetch, apiRequest, ApiError } from './client';

export type AiCardId = 'market_read' | 'drivers' | 'watch' | 'sources' | string;
export type AiStreamEventType = 'status' | 'skill_start' | 'reasoning_start' | 'reasoning_delta' | 'reasoning_end' | 'card_delta' | 'final' | 'done' | 'error';
export type AiGeneratedReactionValue = 'like' | 'dislike';
export type AiCardStatus = 'pending' | 'streaming' | 'complete';

export interface AiChartContext {
  symbol: string;
  interval?: string;
}

export interface AiCard {
  id: AiCardId;
  title: string;
  content: string;
  citations?: string[];
  status?: AiCardStatus;
}

export interface AiResult {
  run_id?: string | null;
  session_id?: string | null;
  language?: string;
  analysis_mode?: string;
  cards?: AiCard[];
  selected_skills?: string[];
}

export interface AiSession {
  id: string;
  status: string;
  title: string;
  symbol?: string;
  interval: string;
  language: string;
  analysis_mode: string;
  last_message_at?: string;
  created_at: string;
  updated_at: string;
  message_count: number;
  last_run_id?: string;
  last_run_status?: string;
  last_run_started_at?: string;
}

export type AiRunStatus = 'running' | 'succeeded' | 'failed' | 'canceled';

export interface AiRunCreateResponse {
  run_id: string;
  session_id: string;
  status: AiRunStatus;
  existing?: boolean;
}

export interface AiRunStatusResponse {
  run_id: string;
  session_id?: string | null;
  status: AiRunStatus;
  result?: AiResult;
  error?: string | null;
  started_at?: string | null;
  finished_at?: string | null;
}

export interface AiRunEvent {
  sequence: number;
  event: AiStreamEventType;
  data: Record<string, unknown>;
  created_at?: string;
}

export interface AiMessage {
  id: string;
  session_id: string;
  run_id?: string;
  role: 'user' | 'assistant' | 'system' | 'tool' | string;
  content: string;
  created_at: string;
}

export type AiGeneratedReactionTarget =
  | { target_type: 'message'; session_id: string; message_id: string }
  | { target_type: 'card'; session_id: string; card_key: string };

export type AiGeneratedReaction = AiGeneratedReactionTarget & { reaction: AiGeneratedReactionValue };

export interface AiMessagesResponse {
  messages: AiMessage[];
  cards?: AiCard[];
  reactions?: AiGeneratedReaction[];
}

export interface AiStatusEvent {
  type: 'status';
  message: string;
  phase?: string;
  title?: string;
}

export interface AiSkillStartEvent {
  type: 'skill_start';
  skill: string;
}

export interface AiReasoningEvent {
  type: 'reasoning_start' | 'reasoning_delta' | 'reasoning_end';
  id: string;
  text?: string;
}

export interface AiCardDeltaEvent {
  type: 'card_delta';
  card?: string;
  result?: AiResult;
  text?: string;
}

export interface AiFinalEvent {
  type: 'final';
  result: AiResult;
}

export interface AiDoneEvent {
  type: 'done';
}

export interface AiErrorEvent {
  type: 'error';
  code?: string;
  message: string;
}

export type AiStreamEvent = AiStatusEvent | AiSkillStartEvent | AiReasoningEvent | AiCardDeltaEvent | AiFinalEvent | AiDoneEvent | AiErrorEvent;

export function createAiAnalyzeRun(chart: AiChartContext): Promise<AiRunCreateResponse> {
  return apiFetch<AiRunCreateResponse>('/api/ai/analyze', {
    method: 'POST',
    body: JSON.stringify({ chart: compactChart(chart) }),
  });
}

export function createAiMessageRun(chart: AiChartContext, message: string, sessionId: string | null | undefined): Promise<AiRunCreateResponse> {
  return apiFetch<AiRunCreateResponse>('/api/ai/message', {
    method: 'POST',
    body: JSON.stringify({
      chart: compactChart(chart),
      message,
      ...(sessionId ? { session_id: sessionId } : {}),
    }),
  });
}

export function fetchAiRun(runId: string, signal?: AbortSignal): Promise<AiRunStatusResponse> {
  return apiFetch<AiRunStatusResponse>(`/api/ai/runs/${encodeURIComponent(runId)}` as `/api/${string}`, signal ? { signal } : {});
}

export function fetchAiRunEvents(runId: string, after = 0, signal?: AbortSignal): Promise<{ events: AiRunEvent[] }> {
  return apiFetch<{ events: AiRunEvent[] }>(`/api/ai/runs/${encodeURIComponent(runId)}/events?after=${after}` as `/api/${string}`, signal ? { signal } : {});
}

export async function streamAiAnalyze(chart: AiChartContext, handlers: AiStreamHandlers, signal?: AbortSignal): Promise<void> {
  await streamAi('/api/ai/analyze/stream', { chart: compactChart(chart) }, handlers, signal);
}

export async function streamAiMessage(chart: AiChartContext, message: string, sessionId: string | null | undefined, handlers: AiStreamHandlers, signal?: AbortSignal): Promise<void> {
  await streamAi('/api/ai/message/stream', {
    chart: compactChart(chart),
    message,
    ...(sessionId ? { session_id: sessionId } : {}),
  }, handlers, signal);
}

export function fetchAiSessions(options: { symbol?: string; interval?: string; limit?: number } = {}): Promise<{ sessions: AiSession[] }> {
  const params = new URLSearchParams();
  if (options.symbol) params.set('symbol', options.symbol);
  if (options.interval) params.set('interval', options.interval);
  if (options.limit) params.set('limit', String(options.limit));
  const query = params.toString();
  return apiFetch<{ sessions: AiSession[] }>(query ? `/api/ai/sessions?${query}` as `/api/${string}` : '/api/ai/sessions');
}

export function fetchAiMessages(sessionId: string, limit = 50): Promise<AiMessagesResponse> {
  return apiFetch<AiMessagesResponse>(`/api/ai/sessions/${encodeURIComponent(sessionId)}/messages?limit=${limit}` as `/api/${string}`);
}

export function archiveAiSession(sessionId: string): Promise<void> {
  return apiRequest(`/api/ai/sessions/${encodeURIComponent(sessionId)}/archive` as `/api/${string}`, { method: 'POST' });
}

export function setAiGeneratedReaction(target: AiGeneratedReactionTarget, reaction: AiGeneratedReactionValue): Promise<void> {
  return apiRequest('/api/ai/generated-reactions', {
    method: 'PUT',
    body: JSON.stringify({ ...target, reaction }),
  });
}

export function clearAiGeneratedReaction(target: AiGeneratedReactionTarget): Promise<void> {
  return apiRequest('/api/ai/generated-reactions', {
    method: 'DELETE',
    body: JSON.stringify(target),
  });
}

interface AiStreamHandlers {
  onEvent(event: AiStreamEvent): void;
}

async function streamAi(path: `/api/${string}`, body: unknown, handlers: AiStreamHandlers, signal?: AbortSignal): Promise<void> {
  const response = await fetch(apiUrl(path), {
    method: 'POST',
    credentials: 'same-origin',
    headers: { Accept: 'text/event-stream', 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    signal,
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => null) as { error?: string; message?: string } | null;
    throw new ApiError(response.status, payload?.message || payload?.error || `HTTP ${response.status}`);
  }
  if (!response.body) throw new Error('Streaming response is unavailable');

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const parts = buffer.split(/\n\n/);
    buffer = parts.pop() ?? '';
    for (const part of parts) {
      const event = parseSSE(part);
      if (event) handlers.onEvent(event);
    }
  }
  const last = parseSSE(buffer.trim());
  if (last) handlers.onEvent(last);
}

function parseSSE(chunk: string): AiStreamEvent | null {
  if (!chunk) return null;
  let eventName = 'message';
  const dataLines: string[] = [];
  for (const line of chunk.split(/\n/)) {
    if (line.startsWith('event:')) eventName = line.slice(6).trim();
    else if (line.startsWith('data:')) dataLines.push(line.slice(5).trimStart());
  }
  const rawData = dataLines.join('\n');
  const data = rawData ? safeJSON(rawData) : {};
  switch (eventName) {
    case 'status':
      return { type: 'status', message: stringValue(data.message, 'Working'), phase: stringValue(data.phase), title: stringValue(data.title) };
    case 'skill_start':
      return { type: 'skill_start', skill: stringValue(data.skill, 'agent') };
    case 'reasoning_start':
      return { type: 'reasoning_start', id: stringValue(data.id, 'reasoning') };
    case 'reasoning_delta':
      return { type: 'reasoning_delta', id: stringValue(data.id, 'reasoning'), text: stringValue(data.text) };
    case 'reasoning_end':
      return { type: 'reasoning_end', id: stringValue(data.id, 'reasoning') };
    case 'card_delta':
      return { type: 'card_delta', card: stringValue(data.card), result: objectValue<AiResult>(data.result), text: stringValue(data.text) };
    case 'final':
      return { type: 'final', result: objectValue<AiResult>(data.result) ?? {} };
    case 'done':
      return { type: 'done' };
    case 'error':
      return { type: 'error', code: stringValue(data.code), message: stringValue(data.message, 'AI response failed') };
    default:
      return null;
  }
}

function safeJSON(value: string): Record<string, unknown> {
  try {
    const parsed = JSON.parse(value) as unknown;
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed as Record<string, unknown> : {};
  } catch {
    return {};
  }
}

function compactChart(chart: AiChartContext): AiChartContext {
  return { symbol: chart.symbol, ...(chart.interval ? { interval: chart.interval } : {}) };
}

function stringValue(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

function objectValue<T>(value: unknown): T | undefined {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as T : undefined;
}
